import { useCallback, useEffect, useRef, useState } from "react";
import {
  formatPreviewDuration,
  formatPreviewTime,
  getPreviewScrollMetrics,
  getScrollRate,
} from "./previewPlaybackUtils";
import {
  getEndHoldVisualState,
  getScrollTimeline,
  getScrollTimelineState,
} from "./scrollEndBehavior";

const PLAYBAR_HIDE_MS = 5000;
// The scroll position still advances on every animation frame. Only the
// React-driven playbar labels/slider are sampled so playback does not rerender
// the entire credits preview 60 times per second.
const PLAYBACK_UI_UPDATE_MS = 100;

const updateEndHoldVisual = (el, state = null, scale = 1) => {
  if (!el) return;
  const wrappers = el.querySelectorAll(".blockWrapper");
  const last = wrappers[wrappers.length - 1];
  wrappers.forEach((wrapper) => {
    if (wrapper === last && state?.isEndHoldTarget) return;
    wrapper.classList.remove("previewEndHoldTarget");
    wrapper.style.removeProperty("--preview-end-hold-offset");
    wrapper.style.removeProperty("--preview-end-hold-opacity");
  });
  el.style.removeProperty("--preview-credits-opacity");
  if (!last || !state?.isEndHoldTarget) return;
  last.classList.add("previewEndHoldTarget");
  last.style.setProperty(
    "--preview-end-hold-offset",
    `${state.holdOffset / Math.max(0.0001, scale)}px`,
  );
  last.style.setProperty(
    "--preview-end-hold-opacity",
    String(state.opacity),
  );
};

/**
 * Scroll live-preview playback: rAF loop, seek/time, speed, and playbar chrome.
 *
 * scrollScale: the display zoom applied to the credits (display width ÷
 * canonical layout width, in editor and fullscreen). Zoom multiplies
 * scrollHeight, so the px/sec rate is multiplied by the same factor —
 * running time stays canonical and identical on every screen.
 */
export default function usePreviewPlayback({
  previewRef,
  ppf,
  playbackFps,
  isRenderPage,
  creditsLength,
  scrollScale = 1,
  layoutVersion,
  endHoldEnabled = false,
}) {
  const effectiveScale = scrollScale > 0 ? scrollScale : 1;

  const isPlayingRef = useRef(false);
  const isLoopingRef = useRef(false);
  const isSeekingRef = useRef(false);
  const rafRef = useRef(null);
  const lastTsRef = useRef(0);
  const lastUiUpdateTsRef = useRef(0);
  const elapsedRef = useRef(0);
  const isPlaybarHoveredRef = useRef(false);
  const isPreviewHoveredRef = useRef(false);
  const playbarHideTimerRef = useRef(null);

  const [isPlaying, setIsPlaying] = useState(false);
  const [isLooping, setIsLooping] = useState(false);
  const [progress, setProgress] = useState(0);
  const [currentSec, setCurrentSec] = useState(0);
  const [durationSec, setDurationSec] = useState(0);
  const [isPlaybarVisible, setIsPlaybarVisible] = useState(true);
  const [isAtLastBlock, setIsAtLastBlock] = useState(false);

  const clearPlaybarHideTimer = useCallback(() => {
    if (playbarHideTimerRef.current) {
      clearTimeout(playbarHideTimerRef.current);
      playbarHideTimerRef.current = null;
    }
  }, []);

  const schedulePlaybarHide = useCallback(() => {
    clearPlaybarHideTimer();
    playbarHideTimerRef.current = setTimeout(() => {
      if (!isPlayingRef.current) return;
      isPlaybarHoveredRef.current = false;
      setIsPlaybarVisible(false);
    }, PLAYBAR_HIDE_MS);
  }, [clearPlaybarHideTimer]);

  const revealPlaybar = useCallback(() => {
    setIsPlaybarVisible(true);
    if (isPlayingRef.current) {
      schedulePlaybarHide();
    } else {
      clearPlaybarHideTimer();
    }
  }, [schedulePlaybarHide, clearPlaybarHideTimer]);

  const hidePlaybar = useCallback(() => {
    if (!isPlayingRef.current) {
      clearPlaybarHideTimer();
      setIsPlaybarVisible(true);
      return;
    }
    isPlaybarHoveredRef.current = false;
    clearPlaybarHideTimer();
    setIsPlaybarVisible(false);
  }, [clearPlaybarHideTimer]);

  const syncProgressFromScroll = useCallback(() => {
    const el = previewRef.current;
    const rate = getScrollRate(ppf, playbackFps) * effectiveScale;
    const { scrollTop } = getPreviewScrollMetrics(el);
    const timeline = getScrollTimeline({ el, rate, endHoldEnabled });
    const elapsed = Math.min(scrollTop, timeline.motionDistance) / rate;
    elapsedRef.current = elapsed;
    updateEndHoldVisual(el);
    setProgress(timeline.duration > 0 ? elapsed / timeline.duration : 0);
    setCurrentSec(elapsed);
    setDurationSec(timeline.duration);

    // Compact once the trailing blank (or last credit) is the main on-screen
    // block, not when an earlier block is centered and the end merely peeks.
    const wrappers = el?.querySelectorAll(".blockWrapper");
    const last =
      el?.querySelector(".previewBlankScreen--end") ||
      wrappers?.[wrappers.length - 1];
    if (!el || !last) {
      setIsAtLastBlock(false);
      return;
    }
    const frame = el.getBoundingClientRect();
    const box = last.getBoundingClientRect();
    const mainLine = frame.top + frame.height * 0.55;
    setIsAtLastBlock(box.top < mainLine && box.bottom > frame.top + 8);
  }, [previewRef, ppf, playbackFps, effectiveScale, endHoldEnabled]);

  useEffect(() => {
    isPlayingRef.current = isPlaying;
  }, [isPlaying]);

  // Auto-hide playbar while hovering the preview container during playback.
  useEffect(() => {
    const host = document.getElementById("preview-container");
    if (!host) return undefined;

    const onEnterOrMove = () => {
      isPreviewHoveredRef.current = true;
      revealPlaybar();
    };
    const onLeave = () => {
      isPreviewHoveredRef.current = false;
      hidePlaybar();
    };

    host.addEventListener("mouseenter", onEnterOrMove);
    host.addEventListener("mousemove", onEnterOrMove);
    host.addEventListener("mouseleave", onLeave);

    if (host.matches(":hover")) {
      isPreviewHoveredRef.current = true;
      revealPlaybar();
    }

    return () => {
      host.removeEventListener("mouseenter", onEnterOrMove);
      host.removeEventListener("mousemove", onEnterOrMove);
      host.removeEventListener("mouseleave", onLeave);
      clearPlaybarHideTimer();
    };
  }, [revealPlaybar, hidePlaybar, clearPlaybarHideTimer]);

  useEffect(() => {
    if (isPlaying) {
      revealPlaybar();
      return undefined;
    }
    clearPlaybarHideTimer();
    setIsPlaybarVisible(true);
    return undefined;
  }, [isPlaying, revealPlaybar, clearPlaybarHideTimer]);

  useEffect(() => {
    if (isRenderPage && isPlaying) {
      setIsPlaying(false);
    }
  }, [isRenderPage, isPlaying]);

  // Keep seek/time in sync with manual scroll and content size changes.
  useEffect(() => {
    const el = previewRef.current;
    if (!el) return;

    const onScroll = () => {
      if (isSeekingRef.current || isPlayingRef.current) return;
      syncProgressFromScroll();
    };

    el.addEventListener("scroll", onScroll, { passive: true });
    syncProgressFromScroll();

    let ro;
    if (window.ResizeObserver) {
      ro = new ResizeObserver(() => syncProgressFromScroll());
      ro.observe(el);
    }

    return () => {
      el.removeEventListener("scroll", onScroll);
      ro?.disconnect();
    };
  }, [previewRef, syncProgressFromScroll, creditsLength, layoutVersion]);

  // rAF loop — time is the source of truth so motion, hold, fade, and seek
  // remain on the same timeline even while scrollTop is stationary.
  useEffect(() => {
    const el = previewRef.current;
    if (!el) return;

    if (!isPlaying) {
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }
      el.style.scrollBehavior = "";
      lastTsRef.current = 0;
      lastUiUpdateTsRef.current = 0;
      return;
    }

    el.style.scrollBehavior = "auto";
    lastTsRef.current = 0;
    lastUiUpdateTsRef.current = 0;

    const tick = (ts) => {
      if (!isPlayingRef.current || isSeekingRef.current) {
        rafRef.current = requestAnimationFrame(tick);
        return;
      }

      if (!lastTsRef.current) {
        lastTsRef.current = ts;
      }
      const dt = Math.min(0.05, (ts - lastTsRef.current) / 1000);
      lastTsRef.current = ts;

      const rate = getScrollRate(ppf, playbackFps) * effectiveScale;
      const timeline = getScrollTimeline({ el, rate, endHoldEnabled });
      if (timeline.duration <= 0) {
        setIsPlaying(false);
        return;
      }

      elapsedRef.current = Math.min(
        timeline.duration,
        elapsedRef.current + dt,
      );
      const state = getScrollTimelineState({
        ...timeline,
        elapsed: elapsedRef.current,
        rate,
        endHoldEnabled,
      });
      el.scrollTop = state.scrollTop;
      const displayedState = getEndHoldVisualState({
        state,
        actualScrollTop: el.scrollTop,
        motionDistance: timeline.motionDistance,
        endHoldEnabled,
      });
      updateEndHoldVisual(el, displayedState, effectiveScale);
      const shouldUpdateUi =
        !lastUiUpdateTsRef.current ||
        ts - lastUiUpdateTsRef.current >= PLAYBACK_UI_UPDATE_MS ||
        state.elapsed >= timeline.duration;
      if (shouldUpdateUi) {
        lastUiUpdateTsRef.current = ts;
        setProgress(state.progress);
        setCurrentSec(state.elapsed);
        setDurationSec(timeline.duration);
      }

      if (state.elapsed >= timeline.duration) {
        if (isLoopingRef.current) {
          el.scrollTop = 0;
          updateEndHoldVisual(el);
          elapsedRef.current = 0;
          lastTsRef.current = ts;
          lastUiUpdateTsRef.current = ts;
          setProgress(0);
          setCurrentSec(0);
          rafRef.current = requestAnimationFrame(tick);
          return;
        }
        setIsPlaying(false);
        return;
      }
      rafRef.current = requestAnimationFrame(tick);
    };

    rafRef.current = requestAnimationFrame(tick);

    return () => {
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }
      el.style.scrollBehavior = "";
    };
  }, [
    isPlaying,
    ppf,
    playbackFps,
    effectiveScale,
    previewRef,
    endHoldEnabled,
  ]);

  const handlePlayPause = useCallback(() => {
    const el = previewRef.current;
    if (!el) return;
    const rate = getScrollRate(ppf, playbackFps) * effectiveScale;
    const timeline = getScrollTimeline({ el, rate, endHoldEnabled });
    if (!isPlaying && elapsedRef.current >= timeline.duration - 0.01) {
      el.style.scrollBehavior = "auto";
      el.scrollTop = 0;
      updateEndHoldVisual(el);
      elapsedRef.current = 0;
      syncProgressFromScroll();
    }
    setIsPlaying((prev) => !prev);
  }, [
    isPlaying,
    previewRef,
    syncProgressFromScroll,
    ppf,
    playbackFps,
    effectiveScale,
    endHoldEnabled,
  ]);

  const handleRestart = useCallback(() => {
    const el = previewRef.current;
    if (!el) return;
    el.style.scrollBehavior = "auto";
    el.scrollTop = 0;
    updateEndHoldVisual(el);
    elapsedRef.current = 0;
    syncProgressFromScroll();
    lastTsRef.current = 0;
    lastUiUpdateTsRef.current = 0;
  }, [previewRef, syncProgressFromScroll]);

  const handleLoopToggle = useCallback(() => {
    setIsLooping((current) => {
      const next = !current;
      isLoopingRef.current = next;
      return next;
    });
  }, []);

  const handleSeekStart = useCallback(() => {
    isSeekingRef.current = true;
  }, []);

  const handleSeek = useCallback(
    (value) => {
      const el = previewRef.current;
      if (!el) return;
      const rate = getScrollRate(ppf, playbackFps) * effectiveScale;
      const timeline = getScrollTimeline({ el, rate, endHoldEnabled });
      const elapsed = value * timeline.duration;
      const state = getScrollTimelineState({
        ...timeline,
        elapsed,
        rate,
        endHoldEnabled,
      });
      el.style.scrollBehavior = "auto";
      el.scrollTop = state.scrollTop;
      const displayedState = getEndHoldVisualState({
        state,
        actualScrollTop: el.scrollTop,
        motionDistance: timeline.motionDistance,
        endHoldEnabled,
      });
      updateEndHoldVisual(el, displayedState, effectiveScale);
      elapsedRef.current = state.elapsed;
      setProgress(state.progress);
      setCurrentSec(state.elapsed);
      setDurationSec(timeline.duration);
    },
    [previewRef, ppf, playbackFps, effectiveScale, endHoldEnabled],
  );

  const handleSeekEnd = useCallback(() => {
    isSeekingRef.current = false;
    lastTsRef.current = 0;
    lastUiUpdateTsRef.current = 0;
  }, []);

  const onBarMouseEnter = useCallback(() => {
    isPlaybarHoveredRef.current = true;
    revealPlaybar();
  }, [revealPlaybar]);

  const onBarMouseLeave = useCallback(() => {
    isPlaybarHoveredRef.current = false;
    if (!isPlayingRef.current) return;
    if (!isPreviewHoveredRef.current) {
      hidePlaybar();
    } else {
      schedulePlaybarHide();
    }
  }, [hidePlaybar, schedulePlaybarHide]);

  return {
    isPlaying,
    isPlayingRef,
    isLooping,
    progress,
    currentLabel: formatPreviewTime(currentSec),
    durationLabel: formatPreviewDuration(durationSec),
    isPlaybarVisible,
    isAtLastBlock,
    syncProgressFromScroll,
    handlePlayPause,
    handleRestart,
    handleLoopToggle,
    handleSeek,
    handleSeekStart,
    handleSeekEnd,
    onBarMouseEnter,
    onBarMouseLeave,
  };
}
